import { CSSProperties } from "react";
import useCampaignBuilder, { CURRENT_BUDGET_TABS, CURRENT_DATE_SELECTION_TABS } from "Hooks/useCampaignBuilder";
import { clsx, makeKey, pluralize, styx } from "modules/Utils";
import CardSymphony from "elements/CardSymphony";
import TagSymphony, { TagColors } from "elements/TagSymphony";
import Colors from "modules/Colors";
import Sty from "modules/Sty";
import StepSymphony from 'elements/StepSymphony';
import { PLAYBOOK_TYPE_INFO, PLAYBOOK_STEP_INFO, PlaybookStepName, PlaybookType, STREAMING_PLATFORMS_INFO } from "modules/Const";
import dayjs from "dayjs";
import Icon from "elements/Icon";
import CreativeTabConnectionDetails_LEFT from "components/connect/CreativeTabConnectionDetails_LEFT";
import { getGenderText } from "modules/Campaign";
import Banner from "elements/Banner";
import DebugBox from "components/shareable/DebugBox";
import { AdConnectionOptions } from "modules/Types";

interface FinalizationCampaignViewProps {
    className?: string;
    style?: CSSProperties;
    isTitle?: boolean;
    variant?: 'facebook' | 'tiktok' | 'playlisting';
}

const FinalizationCampaignView = ({
    className,
    style,
    isTitle = true,
    variant = 'facebook'
}: FinalizationCampaignViewProps) => {
    const {
        campaign,
        playbookStepNames,
        playbookType,
        currentStep,
        setCurrentStep,

        campaignGoal: goal,

        highlightedTrack,
        selectedSongToPromote,
        getAllSelectedPosts,

        budget,
        budgetMaximum,
        startDate,
        endDate,
        numberOfDays,
        currentBudgetTab,
        currentDateSelectionTab,

        linksToPromote,
        enabledPlatforms,

        maxAge,
        minAge,
        genders,
        languages,

        geographyBuckets,

        selectedAudienceGroups,
        getDataForSubmission,
        usesExpressSetup,
        campaignGoal,

        showEditGeographyBucketsBanner,
        getCampaignLength,
        conversionsEnabled,
        conversionsEventName,

        // Playlisting-specific fields
        pitchNotes,
        selectedPlaylistingTier
    } = useCampaignBuilder()

    if (playbookType === null) {
        return null;
    }

    const playbookInfo = PLAYBOOK_TYPE_INFO[playbookType];

    // Mock data for the release type, date, and artist - in a real app this would come from your data source
    const releaseType = "Single";
    const releaseDate = "March 15, 2023";
    const artistName = selectedSongToPromote?.name ? selectedSongToPromote.name.split(' - ')[0] : "Artist Name";

    const getConversionsInfo = () => {
        if (conversionsEnabled && conversionsEventName) {
            return (
                <div className="finalizationCampaignViewLinkSelectorContent">
                    <div className="finalizationCampaignViewLinkSelectorRow">
                        <span style={Sty.text16bold}>Conversions Enabled:</span> {conversionsEnabled ? 'Yes' : 'No'}
                    </div>
                    <div className="finalizationCampaignViewLinkSelectorRow">
                        <span style={Sty.text16bold}>Conversions Event Name:</span> {conversionsEventName}
                    </div>
                </div>
            );
        }
        return null;
    }

    const renderStepContent = (stepName: PlaybookStepName) => {
        switch (stepName) {
            case PlaybookStepName.GOAL_SELECTOR:
                // For playlisting campaigns, show tier information
                if (playbookType === PlaybookType.GET_PLAYLISTED && selectedPlaylistingTier) {
                    return (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 16
                        }}>
                            <div
                                className="finalizationCampaignViewTierSelectorContent"
                                style={sty.finalizationCampaignViewGoalSelectorContent}
                            >
                                <Icon
                                    className="finalizationCampaignViewTierSelectorIcon"
                                    icon="getPlaylistedV2"
                                />
                                <div>
                                    <div style={Sty.text16bold}>
                                        {selectedPlaylistingTier.tier.toLocaleString('en')} Followers
                                    </div>
                                    <div style={Sty.text14grey}>
                                        Pitch to playlists with combined following of over {selectedPlaylistingTier.tier.toLocaleString('en')} followers
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                }

                let additionalInfo = null;
                let icon = "instagram";
                if (playbookType === PlaybookType.TIKTOK_BOOST_ENGAGEMENT) {
                    icon = "tiktok";
                }
                if (campaignGoal?.id === 'promote_a_link') {
                    additionalInfo = (
                        <div className="finalizationCampaignViewLinkSelectorContent">
                            {linksToPromote.map((link, index) => {
                                return (
                                    <div key={makeKey(link.url, index)} className="finalizationCampaignViewLinkSelectorRow">
                                        <span style={Sty.text16bold}>Your Link:</span> <a className="finalizationCampaignViewLinkSelectorLink" href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: Colors.purple, textDecoration: 'underline' }}>{link.url}</a>
                                    </div>
                                )
                            })}
                        </div>
                    );
                }

                let conversionsInfo = getConversionsInfo();
                return (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16
                    }}>
                        <div
                            className="finalizationCampaignViewGoalSelectorContent"
                            style={sty.finalizationCampaignViewGoalSelectorContent}
                        >
                            <Icon
                                className="finalizationCampaignViewGoalSelectorIcon"
                                icon={icon}
                            />
                            <div>
                                {goal?.label}
                            </div>

                        </div>
                        <div>
                            {additionalInfo}
                            {conversionsInfo}
                        </div>
                    </div>
                );
            case PlaybookStepName.RECORD_SELECTOR:


                return (
                    <div
                        className="finalizationCampaignViewRecordSelectorContent"
                        style={sty.releaseContainer}
                    >
                        <div className="releaseMainInfo" style={sty.releaseMainInfo}>
                            <img
                                className="recordSelectorImageSummary"
                                src={selectedSongToPromote?.thumbnail_url}
                                alt={selectedSongToPromote?.name}
                                style={sty.recordSelectorImageSummary}
                            />
                            <div className="releaseDetails" style={sty.releaseDetails}>
                                <div style={{
                                    ...Sty.text16bold,
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {selectedSongToPromote?.name}
                                </div>
                                <div style={sty.releaseMetadata}>
                                    <span style={sty.metadataItem}>{artistName}</span>
                                    <span style={sty.metadataSeparator}>•</span>
                                    <span style={sty.metadataItem}>{releaseType}</span>
                                    <span style={sty.metadataSeparator}>•</span>
                                    <span style={sty.metadataItem}>{releaseDate}</span>
                                </div>
                            </div>
                        </div>

                        {highlightedTrack && (<div>
                            <div className="autoplayingTrackLabel" style={{
                                ...Sty.text16bold,
                            }}>Autoplaying track:</div>
                            <div>
                                {highlightedTrack.name}
                            </div>
                        </div>)}

                        {playbookType === PlaybookType.GET_PLAYLISTED && pitchNotes && (
                            <div style={{ marginTop: 16 }}>
                                <div style={{
                                    ...Sty.text16bold,
                                    marginBottom: 8
                                }}>Pitch Notes:</div>
                                <div style={{
                                    ...Sty.text14grey,
                                    backgroundColor: Colors.greyLighter,
                                    padding: 12,
                                    borderRadius: 8,
                                    fontStyle: 'italic'
                                }}>
                                    "{pitchNotes}"
                                </div>
                            </div>
                        )}
                    </div>
                );
            case PlaybookStepName.CONTENT:
                return (
                    <div className="finalizationCampaignViewContent">
                        <h1>Content</h1>
                    </div>
                );
            case PlaybookStepName.CONNECTIONS:
                if (!usesExpressSetup()) {
                    return (
                        <div className="finalizationCampaignViewConnectionsContent">
                            <CreativeTabConnectionDetails_LEFT
                                className="finalizationCampaignViewConnectionsDetails"
                                overrideExpressSetupOrCustomPages={AdConnectionOptions.custom}
                            />
                        </div>
                    );
                } else {
                    return (
                        <p>Your ads will run through Symphony's page. Your ad budget will be collected upfront.</p>
                    )
                }
            case PlaybookStepName.BUDGET:
                return (
                    <div className="finalizationCampaignViewBudgetContent" style={sty.budgetContainer}>
                        <Banner
                            className="budgetBanner"
                            color="none"
                            style={sty.budgetBanner}
                        >
                            <div className="budgetSection" style={sty.budgetSection}>
                                <div className="budgetLabel" style={sty.budgetLabel}>
                                    {currentBudgetTab === CURRENT_BUDGET_TABS.LIFETIME ? 'Lifetime Budget:' : 'Daily Budget:'}
                                </div>
                                <div className="budgetValue" style={sty.budgetValue}>
                                    ${(Number(budget)).toLocaleString()}
                                    {currentBudgetTab !== CURRENT_BUDGET_TABS.LIFETIME && <span style={sty.perDay}> / day</span>}
                                </div>
                            </div>

                            <div className="budgetDates" style={sty.budgetDates}>
                                <div className="dateSection" style={sty.dateSection}>
                                    <div style={sty.dateLabel}>Start Date:</div>
                                    <div style={sty.dateValue}>{dayjs(startDate).format('MMM D, YYYY')}</div>
                                </div>

                                {currentDateSelectionTab === CURRENT_DATE_SELECTION_TABS.DATE_RANGE && (
                                    <div className="dateSection" style={sty.dateSection}>
                                        <div style={sty.dateLabel}>End Date:</div>
                                        <div style={sty.dateValue}>{dayjs(endDate).format('MMM D, YYYY')}</div>
                                    </div>
                                )}

                                <div className="dateSection" style={sty.dateSection}>
                                    <div style={sty.dateLabel}>
                                        {currentDateSelectionTab === CURRENT_DATE_SELECTION_TABS.MAXIMUM_BUDGET ? 'Maximum Budget:' : 'Campaign Duration:'}
                                    </div>
                                    <div style={sty.dateValue}>
                                        {currentDateSelectionTab === CURRENT_DATE_SELECTION_TABS.MAXIMUM_BUDGET ?
                                            `$${(Number(budgetMaximum)).toLocaleString()}` :
                                            `${getCampaignLength()} days`
                                        }
                                    </div>
                                </div>
                            </div>
                        </Banner>
                    </div>
                );
            case PlaybookStepName.TARGETING:
                let genderText;
                if (genders.length === 1) {
                    genderText = genders[0].name;
                } else if (genders.length === 2) {
                    genderText = genders.map(gender => gender.name).join(' & ');
                } else {
                    genderText = 'All genders';
                }

                const languageText = languages.length > 0 ? languages.map(language => language.name).join(', ') : 'English';

                const showLanguage = variant === 'facebook';
                return (
                    <div className="finalizationCampaignViewTargetingContent" style={sty.targetingContainer}>
                        {/* Demographics section */}
                        {showEditGeographyBucketsBanner &&
                            <Banner
                                className="geographySummaryBanner"
                                color="red"
                                title="No locations selected"
                            >
                                Select Prev and add locations in Targeting
                            </Banner>
                        }
                        {showLanguage && (<div className="targetingSection" style={sty.targetingSection}>
                            <div className="targetingSectionTitle" style={sty.targetingSectionTitle}>
                                Demographics
                            </div>
                            <div className="demographicsDetails" style={sty.demographicsGrid}>
                                <div className="demographicRow" style={sty.demographicRow}>
                                    <span style={sty.demographicLabel}>Ages:</span>
                                    <span style={sty.demographicValue}>{minAge} to {maxAge}</span>
                                </div>
                                <div className="demographicRow" style={sty.demographicRow}>
                                    <span style={sty.demographicLabel}>Gender:</span>
                                    <span style={sty.demographicValue}>{genderText}</span>
                                </div>
                                <div className="demographicRow" style={sty.demographicRow}>
                                    <span style={sty.demographicLabel}>Languages:</span>
                                    <span style={sty.demographicValue}>{languageText}</span>
                                </div>
                            </div>
                        </div>)}

                        {/* Geography section */}
                        <div className="targetingSection" style={sty.targetingSection}>
                            <div className="targetingSectionTitle" style={sty.targetingSectionTitle}>
                                Locations
                            </div>
                            <div className="geographyDetails" style={sty.geographyDetails}>
                                {geographyBuckets.map((bucket, index) => (
                                    <div key={makeKey('geo', index)} className="geographyBucket" style={sty.geographyBucket}>
                                        <div className="bucketHeader" style={sty.bucketHeader}>
                                            <span style={sty.bucketName}>{bucket.name}</span>
                                            <span style={sty.bucketPercentage}>{Math.round(bucket.percentage)}% of budget</span>
                                        </div>
                                        <div className="bucketLocations" style={sty.bucketLocations}>
                                            {bucket.geographies.map((geo, geoIndex) => (
                                                <TagSymphony
                                                    key={makeKey('geo-tag', geoIndex)}
                                                    className="geoTag"
                                                    title={`${geo.name || geo.id} (${geo.type})`}
                                                    color={TagColors.purple}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Audience groups section */}
                        {selectedAudienceGroups.length > 0 && (
                            <div className="targetingSection" style={sty.targetingSection}>
                                <div className="targetingSectionTitle" style={sty.targetingSectionTitle}>
                                    Audience Groups
                                </div>
                                <div className="audienceGroups" style={sty.audienceGroups}>
                                    {selectedAudienceGroups.map((group, index) => (
                                        <div key={makeKey('audience', index)} className="audienceGroup" style={sty.audienceGroup}>
                                            <div className="audienceGroupHeader" style={sty.audienceGroupHeader}>
                                                <Icon
                                                    className="audienceIcon"
                                                    icon="maestroBadgeImage"
                                                    size={20}
                                                />
                                                <span style={sty.audienceGroupName}>{group.title}</span>
                                            </div>
                                            <div className="audienceTags" style={sty.audienceTags}>
                                                {group.tags.map((tag, tagIndex) => (
                                                    <TagSymphony
                                                        key={makeKey('audience-tag', tagIndex)}
                                                        className="audienceTag"
                                                        title={tag.title}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                );
            case PlaybookStepName.LINK_SELECTOR:
                let conversionsInfo2 = getConversionsInfo();

                return (
                    <div className="finalizationCampaignViewLinkSelectorContent">
                        {linksToPromote.map((link, index) => {
                            return (
                                <div key={makeKey(link.url, index)} className="finalizationCampaignViewLinkSelectorRow">
                                    <span style={Sty.text16bold}>Your Link:</span> <a className="finalizationCampaignViewLinkSelectorLink" href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: Colors.purple, textDecoration: 'underline' }}>{link.url}</a>
                                    {conversionsInfo2}
                                </div>
                            )
                        })}
                    </div>
                );
            case PlaybookStepName.PLAYLIST_SELECTOR:
                return (
                    <div className="finalizationCampaignViewPlaylistSelectorContent">
                        <h1>Playlist Selector</h1>
                    </div>
                );
            case PlaybookStepName.PRESAVE_SELECTOR:
                return (
                    <div className="finalizationCampaignViewPresaveSelectorContent">
                        <h1>Presave Selector</h1>
                    </div>
                );
            case PlaybookStepName.CREATIVE:
                let posts = getAllSelectedPosts('facebook');
                if (variant === 'tiktok') {
                    posts = getAllSelectedPosts('tiktok');
                }

                return (
                    <div
                        className="finalizationCampaignViewCreativeContent"
                        style={sty.creativeContainer}
                    >
                        {/* Posts being promoted section */}
                        {posts.length > 0 && (
                            <div className="targetingSection" style={sty.targetingSection}>
                                <div className="targetingSectionTitle" style={sty.targetingSectionTitle}>
                                    {posts.length} {pluralize('Post', posts.length)} being promoted
                                </div>
                                <div className="finalizationCampaignViewPostContainer" style={sty.postsContainer}>
                                    {posts.slice(0, 5).map((post, index) => (
                                        <div
                                            key={makeKey(post.id, index)}
                                            className="finalizationCampaignViewPost"
                                        >
                                            <div
                                                className="finalizationCampaignViewPostImage"
                                                style={{
                                                    ...sty.postImage,
                                                    backgroundImage: `url(${post.thumbnailUrl})`
                                                }}
                                                role="img"
                                                aria-label={post.title}
                                            />
                                        </div>
                                    ))}
                                    {posts.length > 5 && (
                                        <div
                                            className="finalizationCampaignViewMorePosts"
                                            style={{
                                                ...sty.postImage,
                                                backgroundColor: Colors.greyLight,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                ...Sty.text16bold,
                                                color: Colors.greyDark
                                            }}
                                        >
                                            +{posts.length - 5} more
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Links being promoted section - shown as a row of icons */}
                        {enabledPlatforms.length > 0 && (
                            <div className="targetingSection" style={sty.targetingSection}>
                                <div className="targetingSectionTitle" style={sty.targetingSectionTitle}>
                                    Links being promoted
                                </div>
                                <div className="linksToPromoteIconsRow" style={sty.linksIconRow}>
                                    {enabledPlatforms.filter(platform => platform.name !== 'shazam').map((link, index) => {
                                        const streamingInfo = STREAMING_PLATFORMS_INFO[link.name];
                                        if (!streamingInfo) {
                                            return null;
                                        }

                                        return (
                                            <div
                                                key={makeKey(link.name, index)}
                                                style={sty.linkIconWrapper}
                                                onClick={() => window.open(link.url, '_blank')}
                                                title={`View on ${streamingInfo.title}`}
                                            >
                                                <Icon
                                                    className="linkToPromoteIcon"
                                                    icon={streamingInfo.icon}
                                                    size={36}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                );
            default:
                return null;
        }
    }

    const renderFinalizationSteps = () => {
        return (
            <div
                className="finalizationCampaignViewContent"
                style={sty.finalizationCampaignViewContent}
            >
                {playbookStepNames.map((step, index) => {
                    const stepName = step;

                    if (stepName === PlaybookStepName.FINALIZATION) {
                        return null;
                    }

                    // Rename "Record Selector" to "Promote Release"
                    let stepTitle = PLAYBOOK_STEP_INFO[stepName]?.title;
                    if (stepName === PlaybookStepName.RECORD_SELECTOR) {
                        stepTitle = "Promoted Release";
                    } else if (playbookType === PlaybookType.GET_PLAYLISTED && stepName === PlaybookStepName.GOAL_SELECTOR) {
                        stepTitle = "Playlisting Tier";
                    }

                    return (
                        <StepSymphony
                            key={makeKey(stepName, index)}
                            className="innerFinalizationCampaignViewStep"
                            title={stepTitle}
                            type="number"
                            index={index}
                            color={Colors.purpleLighter}
                            onClick={() => setCurrentStep(stepName)}
                            isLastStep={playbookStepNames.length - 2 === index}
                        >
                            {renderStepContent(stepName)}
                        </StepSymphony>
                    )
                })}
            </div>
        )
    }

    return (
        <>
            {/* <DebugBox
                data={getDataForSubmission()}
            /> */}
            <CardSymphony
                className={clsx("finalizationCampaignViewComponent", className)}
                style={style}
                title={isTitle ? "Review your Campaign" : undefined}
                isNoPadding
            >
                <div style={sty.headerContainer}>
                    <div style={sty.headerContent}>
                        <div className="headerRow" style={sty.headerRow}>

                            <TagSymphony
                                className="finalizationCampaignViewTag"
                                title={playbookInfo?.title}
                                icon={playbookInfo?.icon}
                                type="square"
                                style={sty.playbookTypeTag}
                            />
                        </div>
                    </div>
                </div>

                <div style={sty.stepContainer}>
                    {renderFinalizationSteps()}
                </div>
            </CardSymphony>
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    finalizationCampaignViewContent: {
        display: 'flex',
        flexDirection: 'column',
        padding: 16,
        border: Sty.border,
        borderRadius: 16,
        gap: 24
    },
    finalizationCampaignViewGoalSelectorContent: {
        display: 'flex',
        alignItems: 'center',
        gap: 8
    },
    // Release section styles
    releaseContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        width: '100%'
    },
    releaseMainInfo: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
        width: '100%',
    },
    recordSelectorImageSummary: {
        width: 64,
        height: 64,
        borderRadius: 8,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        flexShrink: 0
    },
    releaseDetails: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        flex: '1 1 auto',
        minWidth: 0,
        overflow: 'hidden'
    },
    releaseMetadata: {
        display: 'flex',
        alignItems: 'center',
        color: Colors.greyDark,
        fontSize: 14,
        flexWrap: 'wrap'
    },
    metadataItem: {
        whiteSpace: 'nowrap'
    },
    metadataSeparator: {
        margin: '0 8px'
    },
    // Budget section styles
    budgetContainer: {
        width: '100%'
    },
    budgetBanner: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: 16,
        borderRadius: 8,
        backgroundColor: Colors.purpleLightest,
        border: `1px solid ${Colors.purpleLighter}`
    },
    budgetSection: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 0',
        borderBottom: `1px solid ${Colors.purpleLighter}`
    },
    budgetLabel: {
        fontWeight: 500
    },
    budgetValue: {
        ...Sty.text18bold,
        color: Colors.purple
    },
    perDay: {
        fontWeight: 'normal',
        fontSize: 16
    },
    budgetDates: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12
    },
    dateSection: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    dateLabel: {
        fontWeight: 500
    },
    dateValue: {
        fontWeight: 600
    },
    // Targeting section styles
    targetingContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        width: '100%'
    },
    targetingSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12
    },
    targetingSectionTitle: {
        ...Sty.text16bold,
        borderBottom: `1px solid ${Colors.purpleLighter}`,
        paddingBottom: 8
    },
    demographicsGrid: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8
    },
    demographicRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '6px 0',
        borderBottom: `1px solid ${Colors.purpleLighter}`,
        textAlign: 'right'
    },
    demographicLabel: {
        color: Colors.greyDark
    },
    demographicValue: {
        fontWeight: 600
    },
    geographyDetails: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16
    },
    geographyBucket: {
        padding: 12,
        borderRadius: 8,
        backgroundColor: Colors.purpleLightest,
        border: `1px solid ${Colors.purpleLighter}`
    },
    bucketHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12
    },
    bucketName: {
        fontWeight: 600
    },
    bucketPercentage: {
        color: Colors.purple,
        fontWeight: 500
    },
    bucketLocations: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8
    },
    audienceGroups: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16
    },
    audienceGroup: {
        padding: 12,
        borderRadius: 8,
        backgroundColor: Colors.purpleLightest,
        border: `1px solid ${Colors.purpleLighter}`
    },
    audienceGroupHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 12
    },
    audienceGroupName: {
        fontWeight: 600
    },
    audienceTags: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8
    },
    // Links section styles
    linksIconRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        marginTop: 8
    },
    linkIconWrapper: {
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    headerContainer: {
        display: 'flex',
        width: '100%',
        padding: '4px 16px 12px',
        borderBottom: `1px solid ${Colors.purpleLighter}`,
    },
    headerContent: {
        marginLeft: 'auto',
    },
    stepContainer: {
        padding: '16px 16px 16px',
    },
    creativeContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        width: '100%'
    },
    postsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8
    },
    postImage: {
        width: 100,
        height: 100,
        borderRadius: 8,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    headerRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%'
    },
    playbookTypeTag: {
        marginLeft: 16
    },
}

export default FinalizationCampaignView;